import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiSearch } from 'react-icons/fi';

const PAGE_SIZE = 12;

export default function DashboardDataTable({
  rows = [],
  columns = [],
  onRowClick,
  emptyState,
  searchPlaceholder = 'Search…',
  initialSort,
  totalRows,
  pageSize = PAGE_SIZE,
  page: controlledPage,
  onPageChange,
  sort: controlledSort,
  onSortChange,
  query: controlledQuery,
  onQueryChange,
  manualMode = false,
}) {
  const [queryState, setQueryState] = useState('');
  const [pageState, setPageState] = useState(0);
  const [sortState, setSortState] = useState(initialSort || { key: columns[0]?.key, dir: 'desc' });

  const query = manualMode ? controlledQuery || '' : queryState;
  const page = manualMode ? controlledPage || 0 : pageState;
  const sort = useMemo(
    () =>
      manualMode
        ? controlledSort || initialSort || { key: columns[0]?.key, dir: 'desc' }
        : sortState,
    [columns, controlledSort, initialSort, manualMode, sortState]
  );

  const filtered = useMemo(() => {
    if (manualMode) return rows;
    const q = query.trim().toLowerCase();
    let r = rows;
    if (q) {
      r = rows.filter((row) =>
        columns.some((c) => {
          if (!c.searchable) return false;
          const v = c.accessor ? c.accessor(row) : row[c.key];
          return String(v ?? '').toLowerCase().includes(q);
        })
      );
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      if (col) {
        const get = col.sortAccessor || col.accessor || ((row) => row[col.key]);
        r = [...r].sort((a, b) => {
          const va = get(a);
          const vb = get(b);
          if (typeof va === 'number' && typeof vb === 'number') {
            return sort.dir === 'asc' ? va - vb : vb - va;
          }
          const sa = String(va ?? '');
          const sb = String(vb ?? '');
          return sort.dir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
        });
      }
    }
    return r;
  }, [rows, query, sort, columns, manualMode]);

  const resolvedTotalRows = manualMode ? Number(totalRows || 0) : filtered.length;
  const pageCount = Math.max(1, Math.ceil(resolvedTotalRows / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const slice = manualMode
    ? rows
    : filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const updatePage = (nextPage) => {
    if (manualMode) {
      onPageChange?.(nextPage);
      return;
    }
    setPageState(nextPage);
  };

  const updateQuery = (nextQuery) => {
    if (manualMode) {
      onQueryChange?.(nextQuery);
      onPageChange?.(0);
      return;
    }
    setQueryState(nextQuery);
    setPageState(0);
  };

  const handleSort = (col) => {
    if (!col.sortable) return;
    const nextSort =
      sort.key === col.key
        ? { key: col.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, dir: 'desc' };
    if (manualMode) {
      onSortChange?.(nextSort);
      onPageChange?.(0);
      return;
    }
    setSortState(nextSort);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center gap-2">
        <div className="relative flex-1">
          <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => updateQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 w-full rounded-lg border border-zinc-200 bg-white pl-8 pr-2 text-xs outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
          />
        </div>
        <span className="text-[11px] text-zinc-500">
          {resolvedTotalRows.toLocaleString('en-US')} rows
        </span>
      </div>

      <div className="flex-1 overflow-auto rounded-lg border border-zinc-200">
        <table className="min-w-full text-left text-xs">
          <thead className="sticky top-0 z-10 bg-zinc-50 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => handleSort(c)}
                  className={`whitespace-nowrap px-3 py-2 ${c.sortable ? 'cursor-pointer select-none hover:text-zinc-900' : ''} ${c.align === 'right' ? 'text-right' : ''}`}
                  style={{ width: c.width }}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.header}
                    {c.sortable && sort.key === c.key ? (
                      sort.dir === 'asc' ? <FiChevronUp className="h-3 w-3" /> : <FiChevronDown className="h-3 w-3" />
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 bg-white">
            {slice.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-10">
                  {emptyState || <div className="text-center text-xs text-zinc-500">No rows match.</div>}
                </td>
              </tr>
            ) : (
              slice.map((row, idx) => (
                <tr
                  key={row._id || row.id || idx}
                  onClick={() => onRowClick?.(row)}
                  className={`${onRowClick ? 'cursor-pointer hover:bg-zinc-50' : ''}`}
                >
                  {columns.map((c) => {
                    const v = c.cell ? c.cell(row) : (c.accessor ? c.accessor(row) : row[c.key]);
                    return (
                      <td
                        key={c.key}
                        className={`whitespace-nowrap px-3 py-2 align-middle text-zinc-800 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}
                      >
                        {v}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 ? (
        <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
          <span>
            Page {safePage + 1} of {pageCount}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={safePage === 0}
              onClick={() => updatePage(Math.max(0, safePage - 1))}
              className="rounded-md border border-zinc-200 bg-white px-2 py-1 hover:bg-zinc-50 disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              disabled={safePage >= pageCount - 1}
              onClick={() => updatePage(Math.min(pageCount - 1, safePage + 1))}
              className="rounded-md border border-zinc-200 bg-white px-2 py-1 hover:bg-zinc-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
